import { capitalize } from "./commonFunctions";
import { REGEX } from "./validation";

export { REGEX };

export type ValidationType = "name" | "email" | "phone" | "internationalPhone" | "password" | "pan" | "number" | "text" | "message";

type ValidatorFn = (value: string) => boolean;

export type ErrorMap = Record<string, string>;

type Mode = "change" | "blur";

type HandleFieldParams = {
  type: ValidationType;
  value: string;
  field: string;
  mode: Mode;
  setValue?: (val: string) => void;
  setErrors: React.Dispatch<React.SetStateAction<ErrorMap>>;
  message?: string; // ✅ optional override
  min?: number;
  max?: number;
  allowSpace?: boolean;
  prevValue?: string;
  validateOnChange?: boolean;
};

/* ---------------- Validators ---------------- */
const validators: Record<ValidationType, ValidatorFn> = {
  name: (value) => REGEX.name.test(value.trim()),
  email: (value) => REGEX.email.test(value),
  phone: (value) => REGEX.phone.test(value),
  internationalPhone: (value) => REGEX.internationalPhone.test(value),
  password: (value) => REGEX.password.test(value),
  pan: (value) => REGEX.pan.test(value.trim()),
  number: (value) => REGEX.number.test(value),
  text: (value) => REGEX.text.test(value.trim()),
  message: (value) => value.trim().length > 0,
};

/* ---------------- Default Messages ---------------- */
const defaultMessages: Record<ValidationType, string> = {
  name: "Enter a valid name",
  email: "Enter a valid email",
  phone: "Enter valid phone number",
  internationalPhone: "Enter valid phone number",
  password: "Password must be strong",
  pan: "Enter valid PAN",
  number: "Only numbers allowed",
  text: "Only text allowed",
  message: "Enter a valid message",
};

/* ---------------- Core Helpers ---------------- */
const normalize = (
  type: ValidationType,
  value: string,
  allowSpace?: boolean,
  min?: number,
  max?: number,
  prevValue?: string,
): string => {
  let v = value;

  /* ---------------- REMOVE SPACES ---------------- */
  if (type === "phone" || type === "pan" || type === "email" || type === "number") {
    if (!allowSpace) {
      v = v.replace(/\s/g, "");
    }
  }

  /* ---------------- Email All Small Characters ---------------- */
  if (type === "email") {
    v = v.toLowerCase().trim();
  }

  /* ---------------- NAME / TEXT ---------------- */
  if (type === "name") {
    v = v
      .replace(/\s+/g, " ")
      .trimStart()
      .split(" ")
      .map((word) => capitalize(word))
      .join(" ");
  }

  if (type === "text" || type === "message") {
    v = v.replace(/\s+/g, " ").trimStart();
  }

  /* ---------------- PHONE ---------------- */
  if (type === "phone") {
    v = v.replace(/\D/g, "").slice(0, 10); // ✅ hard limit 10 digits
  }

  /* ---------------- NUMBER ---------------- */
  if (type === "number") {
    v = v.replace(/\D/g, "");

    // ✅ empty → default 0
    if (v === "") return "0";

    // ✅ replace leading 0
    if (prevValue === "0") {
      v = v.replace(/^0+/, "");
    }

    const num = Number(v);

    // ✅ prevent typing beyond max
    if (max !== undefined && num > max) {
      return prevValue ?? String(max);
    }

    return v;
  }

  /* ---------------- PAN ---------------- */
  if (type === "pan") {
    v = v.toUpperCase().slice(0, 10);
  }

  return v;
};

export const validateInputData = (type: ValidationType, value: string, min?: number, max?: number): boolean => {
  const fn = validators[type];
  const isValid = fn ? fn(value) : true;

  if (!isValid) return false;

  // ✅ number min/max check
  if (type === "number" && value) {
    const num = Number(value);

    if (min !== undefined && num < min) return false;
    if (max !== undefined && num > max) return false;
  }

  return true;
};

/* ---------------- Unified Handler ---------------- */
export const handleField = ({
  type,
  value,
  field,
  mode,
  setValue,
  setErrors,
  message,
  min,
  max,
  allowSpace,
  prevValue,
  validateOnChange = false,
}: HandleFieldParams) => {
  if (mode === "change") {
    const normalized = normalize(type, value, allowSpace, min, max, prevValue);

    if (setValue && normalized !== prevValue) {
      setValue(normalized);
    }

    if (validateOnChange) {
      const isValid = validateInputData(type, normalized, min, max);

      setErrors((prev) => ({
        ...prev,
        [field]: isValid ? "" : message || defaultMessages[type],
      }));
    } else {
      setErrors((prev) => {
        if (!prev[field]) return prev;
        return { ...prev, [field]: "" };
      });
    }
  }

  if (mode === "blur") {
    const normalized = normalize(type, value, allowSpace, min, max, prevValue);
    if (!normalized.trim()) return;

    const isValid = validateInputData(type, normalized, min, max);

    if (!isValid) {
      setErrors((prev) => ({
        ...prev,
        [field]:
          message ||
          (type === "number" && (min !== undefined || max !== undefined)
            ? `Value must be between ${min ?? "-∞"} and ${max ?? "∞"}`
            : defaultMessages[type]),
      }));
    }
  }
};
